import { useMemo } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { Filters } from '@rossum/components/filtering';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import SearchInput from '@rossum/components/search-input/SearchInput';
import { TILE_HEIGHT, TileSkeleton } from '@rossum/components/tile';
import { EmptyStateRadar } from '@rossum/ui';
import { IconSearch } from '@rossum/ui/icons/tabler';
import {
  Alert,
  Button,
  Skeleton,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { linebreak, routerLink } from '../../../lib/formaterValues';
import { useStoredQuery } from '../../../utils/hooks/useStoredQuery';
import { getMdhTokenOverride } from '../api';
import { DatasetsHeader } from '../header';
import { useOperations } from '../hooks/useOperations';
import { useTables } from '../hooks/useTables';
import { datasetCreateRoute } from '../routes';
import { Dataset } from '../types';
import { DatasetTile } from './components/DatasetTile';
import {
  filterDatasets,
  getDatasetType,
  OperationsByDataset,
} from './filterDatasets';
import {
  datasetListQueryParser,
  useDatasetListFilters,
} from './useDatasetListFilters';

export const DatasetList = () => {
  const intl = useIntl();
  const history = useHistory();

  const { data: tables, isLoading, isError } = useTables();

  const {
    data: operations,
    isLoading: isOperationsLoading,
    isError: isOperationsError,
  } = useOperations({
    limit: 300,
  });

  const [filters, setFilters] = useStoredQuery({
    parser: datasetListQueryParser,
    localStorageKey: undefined,
  });

  const filterSettings = useDatasetListFilters({ setFilters });

  const operationsByDataset: OperationsByDataset = useMemo(
    () => R.groupBy(operations ?? [], op => op.dataset_name),
    [operations]
  );

  const filteredTables = useMemo(
    () =>
      filterDatasets({
        datasets: tables ?? [],
        operationsByDataset,
        search: filters.search,
        type: filters.type,
        // Without operations every dataset would look like it has no recent
        // updates, so skip the status filter rather than return wrong results.
        status: isOperationsError ? undefined : filters.status,
      }),
    [
      tables,
      operationsByDataset,
      filters.search,
      filters.type,
      filters.status,
      isOperationsError,
    ]
  );

  // Status is derived from operations, so filtering by it before they load
  // would briefly show every dataset as having no recent updates.
  const isStatusFilterPending = !!filters.status?.length && isOperationsLoading;

  return (
    <PageLayout
      renderHeader={props => (
        <DatasetsHeader
          {...props}
          datasetId={undefined}
          datasetTab={undefined}
          breadcrumbs={[]}
          title={intl.formatMessage({
            id: 'features.datasets.list.header.title',
          })}
          description={intl.formatMessage({
            id: 'features.datasets.list.header.text',
          })}
          buttons={[
            <Button
              key="add-dataset-button"
              data-cy="add-dataset-button"
              variant="contained"
              component={RouterLink}
              to={datasetCreateRoute()}
            >
              {intl.formatMessage({
                id: 'features.datasets.list.header.button.addDataset',
              })}
            </Button>,
          ]}
        />
      )}
    >
      {getMdhTokenOverride() ? (
        <Alert severity="info" sx={{ mx: 4, mt: 2 }}>
          Using master MDH environment.
        </Alert>
      ) : null}
      {isError ? (
        <Stack flex={0.8} p={4} justifyContent="center">
          <EmptyStateRadar
            title={intl.formatMessage({
              id: 'features.datasets.errorList.title',
            })}
            description={intl.formatMessage({
              id: 'features.datasets.errorList.description',
            })}
          />
        </Stack>
      ) : isLoading || isStatusFilterPending ? (
        <DatasetSkeletons />
      ) : tables.length === 0 ? (
        <Stack flex={0.8} p={4} justifyContent="center">
          <EmptyStateRadar
            title={intl.formatMessage({
              id: 'features.datasets.emptyList.title',
            })}
            description={intl.formatMessage(
              { id: 'features.datasets.emptyList.description' },
              {
                link: routerLink({ route: datasetCreateRoute() }),
                linebreak,
              }
            )}
            buttonProps={{
              text: intl.formatMessage({
                id: 'features.datasets.list.header.button.addDataset',
              }),
              onClick: () => history.push(datasetCreateRoute()),
            }}
          />
        </Stack>
      ) : (
        <Stack p={4} spacing={4}>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
          >
            <Filters
              dataCy="dataset-list-filters"
              filters={filters}
              setFilters={setFilters}
              filterSettings={filterSettings}
            />
            <SearchInput
              value={filters.search ?? ''}
              onChange={search => setFilters({ search: search || undefined })}
              placeholder={intl.formatMessage({
                id: 'features.datasets.list.search.placeholder',
              })}
              data-cy="dataset-list-search"
            />
          </Stack>
          {isOperationsError && filters.status?.length ? (
            <Alert severity="warning">
              {intl.formatMessage({
                id: 'features.datasets.list.filters.status.unavailable',
              })}
            </Alert>
          ) : null}
          {filteredTables.length === 0 ? (
            <Stack flex={0.8} justifyContent="center">
              <EmptyStateRadar
                title={intl.formatMessage({
                  id: 'features.datasets.list.noResults.title',
                })}
                description={intl.formatMessage({
                  id: 'features.datasets.list.noResults.description',
                })}
                buttonProps={{
                  text: intl.formatMessage({
                    id: 'features.datasets.list.noResults.clearFilters',
                  }),
                  onClick: () => setFilters(undefined),
                }}
                iconProps={{ Icon: IconSearch, iconSize: 32 }}
              />
            </Stack>
          ) : (
            <DatasetSections
              tables={filteredTables}
              operationsByDataset={operationsByDataset}
            />
          )}
        </Stack>
      )}
    </PageLayout>
  );
};

// Manually-uploaded datasets are shown first, auto-managed ones after, each
// under its own heading.
const DatasetSections = ({
  tables,
  operationsByDataset,
}: {
  tables: readonly Dataset[];
  operationsByDataset: OperationsByDataset;
}) => {
  const { manual = [], managed = [] } = R.groupBy(tables, getDatasetType);

  return (
    <Stack spacing={4}>
      <DatasetSection
        section="manual"
        tables={manual}
        operationsByDataset={operationsByDataset}
      />
      <DatasetSection
        section="managed"
        tables={managed}
        operationsByDataset={operationsByDataset}
      />
    </Stack>
  );
};

const DatasetSection = ({
  section,
  tables,
  operationsByDataset,
}: {
  section: 'manual' | 'managed';
  tables: readonly Dataset[];
  operationsByDataset: OperationsByDataset;
}) => {
  const intl = useIntl();

  if (tables.length === 0) {
    return null;
  }

  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <Typography variant="h5">
          {intl.formatMessage({
            id: `features.datasets.list.section.${section}.title`,
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {intl.formatMessage({
            id: `features.datasets.list.section.${section}.description`,
          })}
        </Typography>
      </Stack>
      <Stack spacing={0.5}>
        {tables.map(table => (
          <DatasetTile
            key={table.name}
            table={table}
            operations={operationsByDataset[table.name]}
          />
        ))}
      </Stack>
    </Stack>
  );
};

const DatasetSkeletons = () => (
  <Stack p={4} spacing={4}>
    <Skeleton
      variant="rectangular"
      height={TILE_HEIGHT - 15}
      width="30%"
      sx={{ alignSelf: 'flex-end' }}
    />
    <Stack spacing={0.5}>
      {Array.from({ length: 10 }, (_, i) => i).map(id => (
        <TileSkeleton
          key={id}
          skeletons={['icon', 'title', 'description']}
          hasTo
        />
      ))}
    </Stack>
  </Stack>
);
